import { clsx, type ClassValue } from 'clsx';
import { extendTailwindMerge } from 'tailwind-merge';

/**
 * Custom tailwind-merge configuration that includes HEN Design System tokens.
 * This ensures custom utilities like rounded-max, rounded-large, etc. are recognized.
 */
const customTwMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      rounded: ['rounded-max'],
      'font-size': [
        // HEN typography tokens
        'text-h1',
        'text-h2',
        'text-h3',
        'text-h4',
        'text-h5',
        'text-h6',
        'text-d1',
        'text-d2',
        'text-d3',
        'text-b1',
        'text-b2',
        'text-b3',
        'text-b4',
        'text-b5',
        'text-l1',
        'text-l2',
      ],
      gap: [
        // HEN spacing tokens
        'gap-xxs',
        'gap-xs',
        'gap-sm',
        'gap-md',
        'gap-lg',
        'gap-xl',
        'gap-2xl',
        'gap-3xl',
        'gap-max',
        'gap-section-break',
      ],
    },
  },
});

/**
 * Combines class names using clsx and merges Tailwind CSS classes with tailwind-merge.
 * This utility handles conditional classes and resolves Tailwind class conflicts.
 * Configured to recognize HEN Design System custom utilities.
 *
 * @param inputs - Class values to combine and merge
 * @returns Merged class string
 *
 * @example
 * cn('px-2 py-1', condition && 'bg-blue-500')
 * cn('px-2', 'px-4') // Returns 'px-4' (tailwind-merge resolves conflict)
 * cn('rounded-md', 'rounded-max') // Returns 'rounded-max' (HEN custom utility)
 */
export const cn = (...inputs: ClassValue[]): string => {
  return customTwMerge(clsx(inputs));
};
